<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';

import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import ToggleSwitch from 'primevue/toggleswitch';
import Message from 'primevue/message';
import type { ISectionConfiguration, ISectionConfigurationResponse } from '@manage/shared-types';
import { useAssessmentsStore } from '@/stores/assessments';

interface Props {
  visible: boolean;
  parentAssessmentId: number | null;
  userName: string | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'save-complete'): void;
}>();

const toast = useToast();
const assessmentsStore = useAssessmentsStore();

const isVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value),
});

// Accommodation data from API
const accommodationData = ref<ISectionConfigurationResponse | null>(null);
const originalAccommodationData = ref<ISectionConfigurationResponse | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
const isSaving = ref(false);

// Helper functions

/**
 * Toggles the access status (enabled/disabled) for a section.
 * When disabled, the section will not be available to the student.
 * @param section - The section configuration to toggle
 */
const toggleAccess = (section: ISectionConfiguration) => {
  section.enabled = !section.enabled;
};

/**
 * Toggles the timer removal state for a section.
 * When the timer is removed (timeout = null), the section has no time limit.
 * When enabled, restores the default timeout value.
 * @param section - The section configuration to toggle
 */
const toggleRemoveTimer = (section: ISectionConfiguration) => {
  if (section.timeout === null) {
    // Restore default timeout
    section.timeout = section.defaultTimeout;
  } else {
    // Remove timer
    section.timeout = null;
  }
};

/**
 * Updates the timeout value for a section from the slider input.
 * @param section - The section configuration to update
 * @param value - The new timeout value in seconds (may be number[] from PrimeVue range sliders)
 */
const updateTimeout = (section: ISectionConfiguration, value: number | number[]) => {
  // PrimeVue Slider can emit number[] for range sliders, but we only use single values
  section.timeout = Array.isArray(value) ? value[0] : value;
};

/**
 * Resets all accommodation settings to their default values from ProductConfiguration.
 * This calls the backend API which performs an atomic reset of all sections.
 * On success, updates the local state to reflect the reset values.
 */
const resetToDefault = async () => {
  if (!props.parentAssessmentId) {
    return;
  }

  isSaving.value = true;
  error.value = null;

  try {
    // Call API to reset to defaults
    const data = await assessmentsStore.resetAccommodationSettings(props.parentAssessmentId);

    // Update local state with the reset data (using structuredClone for robust deep cloning)
    accommodationData.value = structuredClone(data);
    originalAccommodationData.value = structuredClone(data);

    toast.add({
      severity: 'success',
      summary: 'Reset Complete',
      detail: 'All sections have been reset to default values',
      life: 3000,
    });

    // Close modal after successful reset
    handleClose();
  } catch (err) {
    console.error('Reset failed:', err);
    toast.add({
      severity: 'error',
      summary: 'Reset Failed',
      detail: err instanceof Error ? err.message : 'Failed to reset accommodation settings',
      life: 5000,
    });
  } finally {
    isSaving.value = false;
  }
};

/**
 * Saves all changed accommodation settings to the backend.
 * Uses dirty checking to only send API requests for sections that have changed.
 * Handles partial failures gracefully by reporting which updates succeeded/failed.
 */
const handleSave = async () => {
  if (!originalAccommodationData.value || !accommodationData.value) {
    toast.add({
      severity: 'error',
      summary: 'Unable to Save',
      detail: 'Accommodation settings are still loading. Please wait and try again.',
      life: 5000,
    });

    return;
  }

  isSaving.value = true;

  try {
    const updatePromises: Promise<unknown>[] = [];

    // Build a map of original sections by ID for stable comparison
    const originalSectionsById = new Map<
      number,
      (typeof originalAccommodationData.value.aptitudes)[0]['sections'][0]
    >();

    originalAccommodationData.value.aptitudes.forEach((aptitude) => {
      aptitude.sections.forEach((section) => {
        originalSectionsById.set(section.id, section);
      });
    });

    // Compare current data with original to find changes using section ID
    accommodationData.value.aptitudes.forEach((aptitude) => {
      aptitude.sections.forEach((section) => {
        const originalSection = originalSectionsById.get(section.id);

        if (!originalSection) {
          return;
        }

        // Check if enabled status changed
        if (section.enabled !== originalSection.enabled) {
          updatePromises.push(
            assessmentsStore.updateAccommodationSectionStatus(section.id, section.enabled)
          );
        }

        // Check if timeout or other timer settings changed
        if (
          section.timeout !== originalSection.timeout ||
          section.memoryQuestionPromptTimeout !== originalSection.memoryQuestionPromptTimeout ||
          section.memoryQuestionTimeout !== originalSection.memoryQuestionTimeout
        ) {
          updatePromises.push(
            assessmentsStore.updateAccommodationSection(section.id, {
              timeout: section.timeout,
              memoryQuestionPromptTimeout: section.memoryQuestionPromptTimeout,
              memoryQuestionTimeout: section.memoryQuestionTimeout,
            })
          );
        }
      });
    });

    // If no changes were made, just close the modal without showing a toast
    if (updatePromises.length === 0) {
      handleClose();

      return;
    }

    // Wait for all updates to complete, allowing partial success
    const results = await Promise.allSettled(updatePromises);

    // Count successes and failures
    const failures = results.filter((r) => r.status === 'rejected');
    const successes = results.length - failures.length;

    if (failures.length > 0) {
      // Some updates failed - show warning with details
      toast.add({
        severity: 'warn',
        summary: 'Partial Save',
        detail: `${successes} setting(s) saved successfully, ${failures.length} failed`,
        life: 5000,
      });
    } else {
      // All updates succeeded
      toast.add({
        severity: 'success',
        summary: 'Settings Saved',
        detail: `Successfully updated ${updatePromises.length} accommodation setting(s)`,
        life: 5000,
      });
    }

    emit('save-complete');
    handleClose();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Save Failed',
      detail: err instanceof Error ? err.message : 'Failed to save accommodation settings',
      life: 5000,
    });
  } finally {
    isSaving.value = false;
  }
};

/**
 * Closes the modal and clears all local state.
 * Called when the user clicks Cancel or after a successful save.
 */
const handleClose = () => {
  accommodationData.value = null;
  originalAccommodationData.value = null;
  isVisible.value = false;
};

/**
 * Watches for modal open event to fetch accommodation settings from the API.
 * Creates deep clones of the fetched data for editing and dirty tracking.
 */
watch(
  () => props.visible,
  async (newVisible) => {
    if (newVisible && props.parentAssessmentId) {
      // Reset state
      accommodationData.value = null;
      originalAccommodationData.value = null;
      isLoading.value = true;
      error.value = null;

      try {
        // Fetch real data from API
        const data = await assessmentsStore.fetchAccommodationSettings(props.parentAssessmentId);

        // Deep clone for editing (using structuredClone for robust deep cloning)
        accommodationData.value = structuredClone(data);
        // Keep original for comparison on save
        originalAccommodationData.value = structuredClone(data);
      } catch (err) {
        console.error('API Error:', err);
        error.value = err instanceof Error ? err.message : 'Failed to load accommodation settings';
      } finally {
        isLoading.value = false;
      }
    }
  }
);
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :closable="!isSaving"
    :dismissableMask="!isSaving"
    :closeOnEscape="!isSaving"
    :style="{ width: '90vw', height: '90vh' }"
    data-cy="accommodation-settings-dialog"
    @hide="handleClose"
  >
    <template #header>
      <div class="accommodation-modal-header">
        <span>
          AchieveWorks Aptitudes Accommodation Settings:
          <strong>{{ userName }}</strong>
        </span>
      </div>
    </template>

    <div v-if="isLoading" class="loading-container" data-cy="accommodation-loading">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <span>Loading accommodation settings...</span>
    </div>

    <Message v-else-if="error" severity="error" icon="pi pi-exclamation-triangle">
      {{ error }}
    </Message>

    <div v-else class="accommodation-content">
      <!-- Info Message -->
      <Message severity="info" icon="pi pi-info-circle" :closable="false">
        All assessments are set to time out after two hours, regardless of any time limit that may
        be displayed or configured.
      </Message>

      <!-- Table Headers -->
      <div class="accommodation-table">
        <div class="accommodation-table-header">
          <div class="col-assessment">ASSESSMENT</div>
          <div class="col-access">ACCESS</div>
          <div class="col-time-limit">TIME LIMIT</div>
          <div class="col-remove-timer"></div>
        </div>

        <!-- Content rows -->
        <div v-if="accommodationData" class="accommodation-table-body">
          <template
            v-for="(aptitude, aptitudeIndex) in accommodationData.aptitudes"
            :key="aptitude.macroQuestionTypeId"
          >
            <!-- Single section aptitude -->
            <div
              v-if="aptitude.sections.length === 1"
              class="accommodation-row"
              :class="{ 'row-alternate': aptitudeIndex % 2 === 1 }"
            >
              <div class="col-assessment">
                <div class="assessment-name">
                  <span>{{ aptitude.name }}</span>
                </div>
              </div>
              <div class="col-access">
                <Button
                  :icon="aptitude.sections[0].enabled ? 'pi pi-eye' : 'pi pi-eye-slash'"
                  :severity="aptitude.sections[0].enabled ? 'success' : 'secondary'"
                  rounded
                  size="small"
                  :disabled="isSaving"
                  :aria-label="aptitude.sections[0].enabled ? 'Disable access' : 'Enable access'"
                  @click="toggleAccess(aptitude.sections[0])"
                />
                <span>{{
                  aptitude.sections[0].enabled ? 'Access enabled' : 'Access disabled'
                }}</span>
              </div>
              <div class="col-time-limit">
                <div v-if="aptitude.sections[0].timeout !== null" class="time-limit-control">
                  <div
                    :id="`time-display-${aptitude.sections[0].id}`"
                    class="time-display"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {{ Math.round(aptitude.sections[0].timeout / 60) }}:00 min.
                  </div>
                  <input
                    type="range"
                    :value="aptitude.sections[0].timeout"
                    :min="aptitude.sections[0].defaultTimeout ?? 60"
                    :max="(aptitude.sections[0].defaultTimeout ?? 60) * 2"
                    :step="60"
                    :disabled="isSaving"
                    class="time-slider"
                    :aria-label="`Change time limit for ${aptitude.name}`"
                    :aria-describedby="`time-display-${aptitude.sections[0].id}`"
                    :aria-valuetext="`${Math.round(aptitude.sections[0].timeout / 60)} minutes`"
                    @input="
                      updateTimeout(
                        aptitude.sections[0],
                        Number(($event.target as HTMLInputElement).value)
                      )
                    "
                  />
                  <div class="slider-labels">
                    <span>1</span>
                    <span>1.5x</span>
                    <span>2x</span>
                  </div>
                </div>
                <span v-else class="no-timer-text">No timer</span>
              </div>
              <div class="col-remove-timer">
                <ToggleSwitch
                  :modelValue="aptitude.sections[0].timeout === null"
                  :disabled="isSaving"
                  :aria-label="
                    aptitude.sections[0].timeout === null ? 'Enable timer' : 'Remove timer'
                  "
                  @update:model-value="() => toggleRemoveTimer(aptitude.sections[0])"
                />
                <span class="remove-timer-label"> Remove timer </span>
              </div>
            </div>

            <!-- Multi-section aptitude -->
            <template v-else>
              <div
                class="accommodation-row aptitude-header"
                :class="{ 'row-alternate': aptitudeIndex % 2 === 1 }"
              >
                <div class="col-assessment">
                  <span class="aptitude-name">{{ aptitude.name }}</span>
                </div>
                <div class="col-access"></div>
                <div class="col-time-limit"></div>
                <div class="col-remove-timer"></div>
              </div>
              <div
                v-for="(section, index) in aptitude.sections"
                :key="section.id"
                class="accommodation-row section-row"
                :class="{ 'row-alternate': aptitudeIndex % 2 === 1 }"
              >
                <div class="col-assessment">
                  <div class="section-name">
                    <span>Section {{ index + 1 }}</span>
                  </div>
                </div>
                <div class="col-access">
                  <Button
                    :icon="section.enabled ? 'pi pi-eye' : 'pi pi-eye-slash'"
                    :severity="section.enabled ? 'success' : 'secondary'"
                    rounded
                    size="small"
                    :disabled="isSaving"
                    :aria-label="section.enabled ? 'Disable access' : 'Enable access'"
                    @click="toggleAccess(section)"
                  />
                  <span>{{ section.enabled ? 'Access enabled' : 'Access disabled' }}</span>
                </div>
                <div class="col-time-limit">
                  <div v-if="section.timeout !== null" class="time-limit-control">
                    <div
                      :id="`time-display-${section.id}`"
                      class="time-display"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {{ Math.round(section.timeout / 60) }}:00 min.
                    </div>
                    <input
                      type="range"
                      :value="section.timeout"
                      :min="section.defaultTimeout ?? 60"
                      :max="(section.defaultTimeout ?? 60) * 2"
                      :step="60"
                      :disabled="isSaving"
                      class="time-slider"
                      :aria-label="`Change time limit for ${aptitude.name} Section ${index + 1}`"
                      :aria-describedby="`time-display-${section.id}`"
                      :aria-valuetext="`${Math.round(section.timeout / 60)} minutes`"
                      @input="
                        updateTimeout(section, Number(($event.target as HTMLInputElement).value))
                      "
                    />
                    <div class="slider-labels">
                      <span>1</span>
                      <span>1.5x</span>
                      <span>2x</span>
                    </div>
                  </div>
                  <span v-else class="no-timer-text">No timer</span>
                </div>
                <div class="col-remove-timer">
                  <ToggleSwitch
                    :modelValue="section.timeout === null"
                    :disabled="isSaving"
                    :aria-label="section.timeout === null ? 'Enable timer' : 'Remove timer'"
                    @update:model-value="() => toggleRemoveTimer(section)"
                  />
                  <span class="remove-timer-label"> Remove timer </span>
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="accommodation-modal-footer">
        <div class="footer-left">
          <Button
            label="Save"
            icon="pi pi-save"
            :loading="isSaving"
            :disabled="isSaving || isLoading"
            data-cy="accommodation-save-btn"
            @click="handleSave"
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            severity="secondary"
            :disabled="isSaving"
            data-cy="accommodation-cancel-btn"
            @click="handleClose"
          />
        </div>
        <Button
          label="Reset to Default"
          icon="pi pi-refresh"
          :disabled="isSaving || isLoading || !accommodationData"
          data-cy="accommodation-reset-btn"
          @click="resetToDefault"
        />
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.accommodation-modal-header {
  font-size: 1.125rem;
  font-weight: normal;
}

.header-divider {
  width: 100%;
  height: 1px;
  background-color: var(--p-surface-300);
  margin-bottom: 1rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px;
  color: var(--p-text-muted-color);
}

.accommodation-content {
  min-height: 400px;
  border-bottom: 1px solid var(--p-surface-300);

  :deep(.p-message) {
    background: transparent !important;
    border: none !important;
    border-width: 0 !important;
    box-shadow: none !important;
    outline: none !important;
  }

  :deep(.p-message-wrapper) {
    background: transparent !important;
    border: none !important;
    border-width: 0 !important;
    box-shadow: none !important;
    outline: none !important;
  }

  :deep(.p-message-icon) {
    background: transparent !important;
  }

  :deep(.p-message-content) {
    background: transparent !important;
    border: none !important;
  }
}

.accommodation-table {
  width: 100%;

  .accommodation-table-header {
    display: grid;
    grid-template-columns: 2fr 1.5fr 1fr 1fr;
    gap: 1rem;
    padding: 1rem;
    background-color: var(--p-surface-100);
    border-bottom: 2px solid var(--p-surface-300);
    font-weight: 700;
    font-size: 0.875rem;
    text-transform: uppercase;
    color: var(--p-text-color);
  }

  .accommodation-table-body {
    padding: 1rem;
    min-height: 300px;

    .accommodation-row {
      display: grid;
      grid-template-columns: 2fr 1.5fr 1fr 1fr;
      gap: 1rem;
      padding: 1rem;
      border-bottom: 1px solid var(--p-surface-200);
      align-items: center;
      background-color: transparent;

      &:last-child {
        border-bottom: none;
      }

      &.row-alternate {
        background-color: var(--p-surface-50);
      }

      &.aptitude-header {
        font-weight: 600;
        padding-top: 1.5rem;
        padding-bottom: 0.5rem;
      }

      &.section-row {
        .col-assessment {
          padding-left: 2rem;
        }
      }
    }

    .col-assessment {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .assessment-name,
      .aptitude-name {
        font-size: 0.95rem;
        font-weight: 600;
      }

      .assessment-name > span:first-child,
      .aptitude-name {
        font-weight: 600;
      }

      .section-name {
        font-size: 0.9rem;
        color: var(--p-text-muted-color);
      }

      .assessment-name,
      .section-name {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .question-type {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        font-size: 0.75rem;
        color: var(--p-text-muted-color);

        i {
          font-size: 0.75rem;
        }
      }
    }

    .col-access {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
    }

    .col-time-limit {
      font-size: 0.875rem;

      .time-limit-control {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        width: 100%;

        .time-display {
          font-weight: 500;
          font-size: 0.875rem;
          text-align: center;
        }

        .time-slider {
          width: 100%;
          height: 6px;
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;

          // Track styling for webkit browsers (Chrome, Safari)
          &::-webkit-slider-runnable-track {
            width: 100%;
            height: 6px;
            background: var(--p-slate-200);
            @include rounded-corners($xsm-radius);
          }

          // Track styling for Firefox
          &::-moz-range-track {
            width: 100%;
            height: 6px;
            background: var(--p-slate-200);
            @include rounded-corners($xsm-radius);
          }

          // Thumb styling for webkit browsers
          &::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 18px;
            height: 18px;
            background: var(--p-primary-600);
            @include rounded-corners(50%);
            cursor: pointer;
            margin-top: -6px;
            transition: transform 0.15s ease;

            &:hover {
              background: var(--p-primary-700);
              transform: scale(1.15);
            }

            &:active {
              background: var(--p-primary-600);
              transform: scale(1.05);
            }
          }

          // Thumb styling for Firefox
          &::-moz-range-thumb {
            width: 18px;
            height: 18px;
            background: var(--p-primary-color);
            border: none;
            @include rounded-corners(50%);
            cursor: pointer;
            transition: transform 0.15s ease;

            &:hover {
              transform: scale(1.15);
            }

            &:active {
              transform: scale(1.05);
            }
          }

          // Disabled state
          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;

            &::-webkit-slider-thumb {
              cursor: not-allowed;
            }

            &::-moz-range-thumb {
              cursor: not-allowed;
            }
          }
        }

        .slider-labels {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: var(--p-text-muted-color);

          span {
            flex: 1;
            text-align: center;

            &:first-child {
              text-align: left;
            }

            &:last-child {
              text-align: right;
            }
          }
        }
      }

      .no-timer-text {
        color: var(--p-text-muted-color);
      }
    }

    .col-remove-timer {
      display: flex;
      align-items: center;
      gap: 0.35rem;

      .remove-timer-label {
        font-size: 0.875rem;
        cursor: pointer;
      }
    }
  }
}

.accommodation-modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  .footer-left {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }
}
</style>
